import { prefersLessMotion } from './markUsedCelebration';
import { CALENDAR_DAY_START_HOUR } from './calendarConstants';

/**
 * ▶ on a session that isn't now moves its block to now (timer spec step 5), and the block shifts there rather than
 * jumping, so the student sees where it went (Petr, 2026-10-18). Sessions it pushes along shift too.
 *
 * Just the block itself sliding over (Petr, 2026-10-18: "just needs to shift into now, no complicated animations").
 * Recorded frame by frame, what made it glitch was the calendar redrawing under it: the server's answer and the timer
 * starting each froze the page for 250–380 ms mid-slide and redrew the block as a new element, which started over. So
 * the slide gets the page to itself: the start (state/timer/operations.ts) draws the new places at once, lets the block
 * slide while the move saves, and only then applies the server's answer and starts the timer, with the block already
 * in place. Should the calendar redraw it anyway, it carries on from where it is on screen.
 */

const SHIFT_MS = 450;
// Starting gently: the click's own redraw holds up the first few frames, and a slide that set off fast was already
// halfway by the first one seen, which read as a jump.
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
// The longest the start waits on the slide, should something keep it from finishing.
const WAIT_AT_MOST_MS = 900;
const SHIFT_ID = 'shift-to-now';

/** Told to the page as a block starts sliding, for its open popup to slide along (CalendarPopupDialog). */
export const BLOCK_SLIDE_EVENT = 'shovel:block-slide';
export type BlockSlide = { id: string; duration: number; easing: string };

type Point = { x: number; y: number };
type Followed = {
  id: string;
  node: HTMLElement | null;
  laid: Point | null;
  /** Where it was seen before its new place was drawn. */
  from: Point | null;
  /** The slide under way: how far it started from where it is laid out. */
  slide: { animation: Animation; dx: number; dy: number } | null;
};

export const blockEl = (id: string) =>
  document.querySelector(`.fc-timegrid-event[data-planned-task-id="${id}"]`) as HTMLElement | null;

/**
 * Where a block is laid out, in the calendar's scrolled content, so scrolling to it as it slides doesn't count as
 * moving.
 */
const laidOut = (el: HTMLElement): Point | null => {
  const harness = el.parentElement;
  const scroller = el.closest('.fc-scroller') as HTMLElement | null;
  if (!harness || !scroller) return null;
  const box = harness.getBoundingClientRect();
  const view = scroller.getBoundingClientRect();
  return { x: box.left - view.left + scroller.scrollLeft, y: box.top - view.top + scroller.scrollTop };
};

/** Where a block is seen now: where it was laid out, less what its slide has covered. */
const seenNow = (it: Followed): Point | null => {
  if (!it.laid) return it.from;
  if (!it.slide) return it.laid;
  const progress = it.slide.animation.effect?.getComputedTiming().progress;
  const left = progress === null || progress === undefined ? 0 : 1 - progress;
  return { x: it.laid.x + it.slide.dx * left, y: it.laid.y + it.slide.dy * left };
};

// A block sliding to somewhere out of view is scrolled to, smoothly, as it goes.
const scrollTo = (el: HTMLElement) => {
  const scroller = el.closest('.fc-scroller') as HTMLElement | null;
  const harness = el.parentElement;
  if (!scroller || !harness) return;
  const view = scroller.getBoundingClientRect();
  const box = harness.getBoundingClientRect();
  if (box.top >= view.top && box.bottom <= view.bottom) return;
  const top = scroller.scrollTop + box.top - view.top - Math.max(0, (view.height - box.height) / 2);
  scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
};

const slide = (it: Followed, el: HTMLElement, laid: Point, scrollToIt: boolean, sideways = true): Animation | null => {
  const from = it.node && it.laid ? seenNow(it) : it.from;
  const previous = it.slide;
  it.node = el;
  it.laid = laid;
  it.slide = null;
  if (!from) return null;
  let dx = from.x - laid.x;
  const dy = from.y - laid.y;
  // Not sideways within its day: a block drawn narrower or wider as the blocks beside it come and go (a Bumpers drop)
  // only changes its width, and slid in from the side (Petr, 2026-10-24). To another day it still slides across.
  const column = el.closest('.fc-timegrid-col');
  if (!sideways && column && Math.abs(dx) < column.getBoundingClientRect().width / 2) dx = 0;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return null;
  // Redrawn part way, it finishes in the time the slide had left.
  const timing = previous?.animation.effect?.getComputedTiming();
  const left = timing && typeof timing.localTime === 'number' ? SHIFT_MS - timing.localTime : SHIFT_MS;
  const harness = el.parentElement;
  // Over the blocks it passes.
  if (harness) harness.style.zIndex = '20';
  const animation = el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
    duration: Math.max(160, left),
    easing: EASE
  });
  animation.id = SHIFT_ID;
  window.dispatchEvent(
    new CustomEvent<BlockSlide>(BLOCK_SLIDE_EVENT, { detail: { id: it.id, duration: Math.max(160, left), easing: EASE } })
  );
  const done = () => {
    if (harness) harness.style.zIndex = '';
  };
  animation.onfinish = done;
  animation.oncancel = done;
  it.slide = { animation, dx, dy };
  if (scrollToIt) scrollTo(el);
  return animation;
};

/**
 * Follows the blocks of these sessions (the started one first) as the calendar draws their new places: each slides
 * there from where it was. Call before the new places are put in the calendar. Resolves once the started one is in
 * place, at once if there is nothing to slide. `sideways: false` keeps a block from sliding sideways within its day.
 */
export const followBlocks = (ids: (string | number)[], { sideways = true }: { sideways?: boolean } = {}): Promise<void> => {
  const calendar = document.querySelector('.fc') as HTMLElement | null;
  if (prefersLessMotion() || typeof MutationObserver === 'undefined' || !calendar) return Promise.resolve();
  const followed: Followed[] = ids.map(String).map(id => {
    const el = blockEl(id);
    return { id, node: null, laid: null, from: el && laidOut(el), slide: null };
  });
  if (!followed[0]?.from) return Promise.resolve();

  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      // Still followed a moment longer, in case a last redraw moves one.
      window.setTimeout(() => observer.disconnect(), 1500);
      resolve();
    };
    // As the calendar changes, before the change is on screen, so a block is never seen in its new place unslid.
    const check = () =>
      followed.forEach((it, index) => {
        const el = blockEl(it.id);
        const laid = el && laidOut(el);
        if (!el || !laid) return;
        const same = el === it.node && !!it.laid && Math.abs(laid.x - it.laid.x) < 1 && Math.abs(laid.y - it.laid.y) < 1;
        if (same) return;
        const animation = slide(it, el, laid, index === 0, sideways);
        if (index !== 0) return;
        if (animation) animation.finished.then(finish, () => undefined);
        else finish();
      });
    const observer = new MutationObserver(check);
    observer.observe(calendar, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class'] });
    window.setTimeout(finish, WAIT_AT_MOST_MS);
  });
};

// The calendar goes to the week a moment is drawn in when asked (Calendar registers).
const showDateListeners = new Set<(at: number) => void>();
export const onShowDate = (listener: (at: number) => void) => {
  showDateListeners.add(listener);
  return () => {
    showDateListeners.delete(listener);
  };
};

/** Turns the calendar to the week `at` is drawn in. */
export const showDate = (at: number) => showDateListeners.forEach(listener => listener(at));

/**
 * Whether a moment is drawn outside the dates on screen, `from`–`to` as the calendar keeps them (midnights): its days
 * start at 4am, so 1:30am belongs to the day before (Petr, 2026-10-18: started at 1:40am on a Sunday, the block went to
 * the week before and the calendar stayed put). Negative: an earlier week; positive: a later one; 0: on screen.
 */
export const weekSide = (at: number, from: Date | string | null, to: Date | string | null) => {
  if (!from || !to) return 0;
  const shiftMs = CALENDAR_DAY_START_HOUR * 60 * 60 * 1000;
  // The calendar keeps the end with the night of its last day already in it (Saturday 28:00).
  if (at < new Date(from).getTime() + shiftMs) return -1;
  if (at >= new Date(to).getTime()) return 1;
  return 0;
};
